import React from "react";
import ReplicaTour from "./ReplicaTour";
import { MASTER_ENABLER_STEPS, MasterEnablerPane, MASTER_ENABLER_SECTION, DeleteConfirmDialog } from "./masterEnabler";
import { Download, FileSpreadsheet, Plus, Search, Trash2, Pencil, Cpu, HelpCircle,
  HandCoins, Wallet, CalendarClock, ArrowLeftRight, Server, Upload, Undo2, HardDriveDownload,
  Percent, Bitcoin, Landmark, Building2, Boxes, Sprout, PiggyBank, History, Users, AlertTriangle,
  Target, FolderTree, Paperclip } from "lucide-react";

const ICONS = { HandCoins, Wallet, CalendarClock, ArrowLeftRight, Percent, History, Users, Bitcoin,
  Landmark, Building2, Boxes, Sprout, PiggyBank, Target, FolderTree, AlertTriangle };

const Chip = ({ id, icon, label, active, P }) => {
  const Icon = ICONS[icon] || Boxes;
  return (
    <span data-rid={id} className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] ${active ? "border-transparent bg-[var(--primary)] text-white" : "border-black/15 dark:border-white/15"}`}>
      <Icon size={11} /> {P ? P(label) : label}
    </span>
  );
};

// Faithful mock of the shared ResourcePage screen used by SEBAA / Currencida / BoxAlong.
function ModulePane({ cfg, me, activeRid, P = (t) => t }) {
  return (
    <div className="relative rounded-xl border border-black/10 bg-white text-black shadow-lg dark:border-white/10 dark:bg-neutral-900 dark:text-white">
      <div className={`origin-top transition-all duration-700 ${me ? "scale-[0.97] opacity-40 blur-[1.5px]" : "opacity-100"}`}>
      {/* header */}
      <div className="flex flex-wrap items-center gap-2 border-b border-black/10 px-3 py-2 dark:border-white/10">
        <span className="font-head text-sm font-semibold">{P(cfg.title)}</span>
        <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-600">v1.0</span>
        <div className="ms-auto flex flex-wrap items-center gap-1">
          <div className="inline-flex items-center rounded-md border border-black/15 px-1.5 py-1 dark:border-white/15"><HelpCircle size={13} /></div>
          <div data-rid="rt-manual" className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-[11px] dark:border-white/15"><Download size={12} /> {P("Manual")}</div>
          <div data-rid="rt-csv" className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-[11px] dark:border-white/15"><FileSpreadsheet size={12} /> {P("CSV")}</div>
          <div data-rid="rt-export" className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-[11px] dark:border-white/15"><Download size={12} /> {P("Export CSV/JSON")}</div>
          <div data-rid="rt-attach" className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-[11px] dark:border-white/15"><Paperclip size={12} /> {P("Photos & docs")}</div>
          <div data-rid="rt-add" className="inline-flex items-center gap-1 rounded-md border border-transparent bg-[var(--primary)] px-2 py-1 text-[11px] text-white"><Plus size={12} /> {P("Add")}</div>
        </div>
      </div>
      {/* automation card (+ optional warnings) */}
      <div data-rid="rt-automation" className="mx-3 mt-2 flex items-center gap-2 rounded-md border border-black/10 bg-black/[0.02] px-3 py-1.5 text-[11px] dark:border-white/10 dark:bg-white/[0.03]">
        <Cpu size={13} className="text-[var(--primary)]" /> {cfg.automation}
        {cfg.warnings && <span data-rid="rt-warnings" className="ms-auto inline-flex items-center gap-1 rounded-full bg-[#FF832B]/15 px-1.5 py-0.5 text-[10px] text-[#FF832B]"><AlertTriangle size={10} /> {cfg.warnings}</span>}
        {!cfg.warnings && <span className="ms-auto rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-emerald-600">{P("Active")}</span>}
      </div>
      {/* asset-class / seeder chips (Holdings) */}
      {cfg.assetChips && (
        <div className="mx-3 mt-2 flex flex-wrap items-center gap-1" data-testid="rt-asset-chips">
          <span className="text-[10px] font-semibold uppercase tracking-wide opacity-50">{P("Asset classes")}</span>
          {cfg.assetChips.map((c, i) => <Chip key={c.id} {...c} P={P} active={i === 0} />)}
        </div>
      )}
      {/* secondary controls (categories / history) */}
      {cfg.chips && (
        <div className="mx-3 mt-2 flex flex-wrap items-center gap-1">
          {cfg.chips.map((c) => <Chip key={c.id} {...c} P={P} />)}
        </div>
      )}
      {/* summary tiles (+ optional target) */}
      <div className="mx-3 mt-2 grid grid-cols-4 gap-2">
        {cfg.tiles.map(([label, value], k) => (
          <div key={k} data-rid={k === 0 ? "rt-tile" : undefined} className="rounded-md border border-black/10 px-2 py-1.5 dark:border-white/10">
            <div className="text-[9px] uppercase tracking-wide opacity-50">{P(label)}</div>
            <div className="font-head text-sm">{value}</div>
          </div>
        ))}
      </div>
      {cfg.targetLabel && (
        <div className="mx-3 mt-2">
          <span data-rid="rt-target" className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-[10px] dark:border-white/15"><Target size={11} /> {P(cfg.targetLabel)}</span>
        </div>
      )}
      {/* search */}
      <div className="mx-3 mt-2">
        <div data-rid="rt-search" className="inline-flex w-full items-center gap-1 rounded-md border border-black/15 px-2 py-1.5 text-[11px] opacity-60 dark:border-white/15"><Search size={12} /> {P("Search…")}</div>
      </div>
      {/* grouped tables (multiple sections for realism) */}
      <div className="mx-3 mt-2 mb-3 space-y-2">
        {cfg.sections.map((sec, si) => (
          <div key={si}>
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide opacity-50">{P(sec.group)}</div>
            <div className="overflow-hidden rounded-md border border-black/10 dark:border-white/10">
              <table className="w-full text-[11px]">
                <thead><tr className="border-b border-black/10 dark:border-white/10">
                  {cfg.columns.map((c) => <th key={c} className="p-2 text-start font-medium opacity-60">{P(c)}</th>)}
                  <th className="p-2" />
                </tr></thead>
                <tbody>
                  {sec.rows.map((row, ri) => {
                    const firstRow = si === 0 && ri === 0;
                    return (
                      <tr key={ri} data-rid={firstRow ? "rt-row" : undefined} className="border-b border-black/5 last:border-0 dark:border-white/5">
                        {row.map((cell, ci) => <td key={ci} className="p-2">{cell}</td>)}
                        <td className="p-2">
                          <div className="flex items-center justify-end gap-2 opacity-60">
                            {cfg.actions.map((a) => {
                              const Icon = ICONS[a.icon];
                              return (
                                <span key={a.id} data-rid={firstRow ? a.id : undefined} className="inline-flex items-center gap-0.5 whitespace-nowrap text-[10px]">
                                  <Icon size={11} /> {P(a.label)}
                                </span>
                              );
                            })}
                            <span data-rid={firstRow ? "rt-row-edit" : undefined} className="inline-flex items-center gap-1"><Pencil size={11} /><Trash2 size={11} /></span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
      </div>
      {/* Master Enabler mini-panel */}
      <div style={me ? { backgroundColor: "color-mix(in srgb, var(--primary) 8%, transparent)" } : undefined}
        className={`mx-3 mb-3 rounded-md border p-2 transition-all duration-700 ${me ? "scale-[1.03] border-[var(--primary)] shadow-lg" : "border-black/10 bg-black/[0.02] dark:border-white/10 dark:bg-white/[0.03]"}`}>
        <div className="mb-1.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide opacity-55"><Server size={11} className="text-[var(--primary)]" /> {P("Master Enabler")}</div>
        <div className="flex flex-wrap items-center gap-1">
          <span data-rid="rt-cluster-export" className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-[10px] dark:border-white/15"><Upload size={11} /> {P("Cluster: Export")}</span>
          <span data-rid="rt-cluster-import" className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-[10px] dark:border-white/15"><Download size={11} /> {P("Cluster: Import")}</span>
          <span data-rid="rt-recent" className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-[10px] dark:border-white/15"><Undo2 size={11} /> {P("Recently moved")}</span>
          <span data-rid="rt-download" className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-[10px] dark:border-white/15"><HardDriveDownload size={11} /> {P("Download all data")}</span>
          <span data-rid="rt-delete" className="inline-flex items-center gap-1 rounded-md border border-[#DA1E28]/40 px-2 py-1 text-[10px] text-[#DA1E28]"><Trash2 size={11} /> {P("Delete customer data")}</span>
        </div>
      </div>
      {activeRid === "rt-delete-confirm" && <DeleteConfirmDialog confirmRid="rt-delete-confirm" />}
    </div>
  );
}

const CFG = {
  benefits: {
    title: "Benefits Management", label: "SEBAA™ — Benefits",
    automation: "FopFo Automation · Pay status · Connected: Currencida™, Familigi™",
    chips: [{ id: "rt-history", icon: "History", label: "Payment history" }],
    tiles: [["Benefits", "3"], ["Monthly", "$8,700"], ["Annual", "$104,400"], ["Pools", "3"]],
    columns: ["Benefit", "Groups", "Eligibility", "Monthly", "Tax"],
    sections: [
      { group: "Health", rows: [["Health cover", "Elders", "Age 60+", "$3,200", "5%"], ["Dental plan", "All staff", "Enrolled", "$600", "0%"]] },
      { group: "Education & Housing", rows: [["Education grant", "Students", "Enrolled", "$1,500", "0%"], ["Housing stipend", "Adults", "Head of house", "$4,000", "5%"]] },
    ],
    actions: [{ id: "rt-pay", icon: "HandCoins", label: "Pay" }, { id: "rt-pay-group", icon: "Users", label: "Pay group" }],
    // 12 steps
    target: ["rt-add", "rt-row-edit", "rt-pay", "rt-pay-group", "rt-history", "rt-csv",
      "rt-automation", "rt-cluster-export", "rt-cluster-import", "rt-recent", "rt-download", "rt-delete-confirm"],
    note: "Faithful replica of the SEBAA™ (Benefits) screen; the hand follows the verbatim manual onto each real control. (English narration; localization to follow.)",
  },
  currency: {
    title: "Currency Management", label: "Currencida™ — Currency",
    automation: "FopFo Automation · Auto top-up", warnings: "1 low-pool warning",
    tiles: [["Currencies", "3"], ["Value (base)", "1.28M"], ["Fees", "$5"], ["Top-up on", "2"]],
    columns: ["Currency", "Type", "Balance", "Rate→base", "Auto top-up"],
    sections: [
      { group: "Fiat", rows: [["US Dollar", "Fiat", "820,400", "1", "off"], ["Pound Sterling", "Fiat", "210,000", "1.62", "off"]] },
      { group: "Mobile money & Stablecoin", rows: [["M-Pesa", "Mobile money", "64,200", "0.0069", "≤ 5,000 → +20,000"], ["USDC", "Stablecoin", "150,000", "1", "≤ 10,000 → +50,000"]] },
    ],
    actions: [{ id: "rt-topup", icon: "Wallet", label: "Top-up" }],
    // 11 steps
    target: ["rt-add", "rt-row-edit", "rt-topup", "rt-warnings", "rt-csv", "rt-automation",
      "rt-cluster-export", "rt-cluster-import", "rt-recent", "rt-download", "rt-delete-confirm"],
    note: "Faithful replica of the Currencida™ (Currency) screen; the hand follows the verbatim manual onto each real control. (English narration; localization to follow.)",
  },
  holdings: {
    title: "Holdings Management", label: "BoxAlong™ — Holdings",
    automation: "FopFo Automation · Draw-down status · Connected: Currencida™",
    assetChips: [
      { id: "rt-crypto", icon: "Bitcoin", label: "Cryptocurrencies & NFTs" },
      { id: "rt-bonds", icon: "Landmark", label: "Bonds & Notes" },
      { id: "rt-realestate", icon: "Building2", label: "Mobility & Real Estate" },
      { id: "rt-altassets", icon: "Boxes", label: "Alternative Assets" },
      { id: "rt-seeders", icon: "Sprout", label: "Land · Options · Trusts · Tangents" },
      { id: "rt-fundseeder", icon: "PiggyBank", label: "3rd-party fund seeder" },
    ],
    chips: [{ id: "rt-category", icon: "FolderTree", label: "Category / Subcategory" }],
    tiles: [["Items", "3"], ["Gross value", "$1.97M"], ["Book value", "$1.90M"], ["Categories", "9"]],
    targetLabel: "Set portfolio target",
    columns: ["Item", "Category", "Asset class", "Gross value", "Book value"],
    sections: [
      { group: "Real estate", rows: [["Lagos apartment", "Real estate", "Real estate", "$1,200,000", "$1,140,000"], ["Beach villa", "Real estate", "Resort", "$540,000", "$521,000"]] },
      { group: "Investments & Collectibles", rows: [["Equity fund", "Investments", "Equity", "$680,000", "$680,000"], ["Art collection", "Collectibles", "Art", "$95,000", "$88,000"]] },
    ],
    actions: [
      { id: "rt-depreciation", icon: "Percent", label: "Depreciation" },
      { id: "rt-schedule", icon: "CalendarClock", label: "Schedule" },
      { id: "rt-transact", icon: "ArrowLeftRight", label: "Transact" },
    ],
    // 20 steps
    target_map: null,
    note: "Faithful replica of the BoxAlong™ (Holdings) screen; the hand follows the verbatim manual onto each real control. (English narration; localization to follow.)",
  },
};
// Holdings uses `target` string for the tile chip, so its step map lives here (20 steps).
CFG.holdings.stepTarget = [
  "rt-add", "rt-category", "rt-depreciation", "rt-schedule", "rt-transact", "rt-row-edit",
  "rt-crypto", "rt-bonds", "rt-realestate", "rt-altassets", "rt-seeders", "rt-fundseeder",
  "rt-target", "rt-search", "rt-automation", "rt-cluster-export", "rt-cluster-import",
  "rt-recent", "rt-download", "rt-delete-confirm",
];

const ME_TARGETS = ["rt-cluster-export", "rt-cluster-import", "rt-recent", "rt-download", "rt-delete", "rt-delete-confirm"];

export default function ModuleReplicaTour({ app }) {
  const cfg = CFG[app];
  if (!cfg) return null;
  const target = app === "holdings" ? cfg.stepTarget : cfg.target;
  const Pane = (props) => <ModulePane cfg={cfg} {...props} />;
  return <ReplicaTour app={app} Pane={Pane} target={target} note={cfg.note} meTargets={ME_TARGETS}
    prefixSteps={MASTER_ENABLER_STEPS} PrefixPane={MasterEnablerPane}
    prefixLabel={MASTER_ENABLER_SECTION} appLabel={cfg.label} />;
}
